import React from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home } from './pages/Home';
import { MyAlpha } from './pages/MyAlpha';
import { Industries } from './pages/Industries';
import { Contact } from './pages/Contact';
import { Particles } from './components/Particles';
import './App.css';

function App() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const navLinks = [
    { to: '/myalpha', label: 'MyAlpha' },
    { to: '/industries', label: 'Industries' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <div className="app">
      <Particles />
      
      {/* Navigation */}
      <motion.nav 
        className="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <motion.div 
              className="logo-icon"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <img src="/rdrlogo.png" alt="RunDuckRun Logo" className="logo-img" />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <ul className="nav-links">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link 
                  to={link.to} 
                  className={`nav-link ${location.pathname === link.to ? 'active' : ''}`}
                >
                  {link.label}
                  {location.pathname === link.to && (
                    <motion.div 
                      className="nav-indicator"
                      layoutId="navIndicator"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>

          <div className="nav-actions">
            <Link to="/contact?source=nav" className="btn btn-primary nav-cta">
              Talk to sales
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className={`menu-toggle ${isMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-nav"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={link.to} 
                    className="mobile-nav-link"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <Link 
                to="/contact?source=nav" 
                className="btn btn-primary mobile-cta"
                onClick={() => setIsMenuOpen(false)}
              >
                Talk to sales
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Main Content */}
      <main className="main-content">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Home />} />
            <Route path="/myalpha" element={<MyAlpha />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/nest" element={<Navigate to="/myalpha" replace />} />
            <Route path="/services" element={<Navigate to="/myalpha" replace />} />
            <Route path="/saas" element={<Navigate to="/myalpha" replace />} />
            <Route path="/solutions" element={<Navigate to="/industries" replace />} />
          </Routes>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/rdrlogo.png" alt="RunDuckRun Logo" className="footer-logo-img" />
                <span className="footer-product">MyAlpha</span>
              </div>
              <p>On-prem language models for industries that can't afford a leak — or a lecture.</p>
            </div>
            
            <div className="footer-links">
              <h4>Product</h4>
              <ul>
                <li><Link to="/myalpha">MyAlpha</Link></li>
                <li><Link to="/myalpha">MyAlpha Rack</Link></li>
                <li><Link to="/myalpha">MyAlpha on NVIDIA</Link></li>
                <li><Link to="/industries">Industries</Link></li>
              </ul>
            </div>
            
            <div className="footer-links">
              <h4>Company</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/industries">Industry packs</Link></li>
                <li><Link to="/contact?source=footer">Contact</Link></li>
              </ul>
            </div>
            
            <div className="footer-contact">
              <h4>Get in Touch</h4>
              <p>
                <a href="tel:+16084213923">(608) 421-3923</a>
              </p>
              <p>
                <a href="mailto:sales@runduckrun.com">sales@runduckrun.com</a>
              </p>
              <Link to="/contact?source=footer" className="btn btn-secondary">
                Talk to sales
              </Link>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} MyAlpha by RunDuckRun. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
